import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'src/lib/forms/Form';
import { getWorkspaceContactGroups } from 'src/selectors/contactGroups';
import WorkspaceSystem from 'src/systems/WorkspaceSystem';
import { ContactGroup } from 'src/types/contactGroup';

const INVITE_WORKSPACE_MEMBERS_ID = 'all-workspace-members';
const WORKSPACE_MEMBERS_GROUP = 'Workspace Members';
const CONTACT_GROUPS_GROUP = 'Contact Groups';

type UseFormInviteMembersOptionsParams = {
	selectedWorkspaceID: number | null,
	isContactGroupsFeatureAvailable: boolean,
	formState: Form,
	setFormState: Dispatch<SetStateAction<Form>>
};

const ALL_WORKSPACE_MEMBERS_OPTION = {
	label: 'All workspace members',
	value: INVITE_WORKSPACE_MEMBERS_ID,
	group: WORKSPACE_MEMBERS_GROUP
};

const NO_CONTACT_GROUPS_OPTION = {
	label: 'There are no collaborator groups available. You can create a new one from the workspace settings.',
	value: 'no-contact-groups',
	disabled: true,
	group: CONTACT_GROUPS_GROUP
};

const mapContactGroupsToOptions = (
	contactGroups: ContactGroup[],
	groupName: string,
	isContactGroupsFeatureAvailable: boolean
) => (
	contactGroups.map( ( contactGroup: ContactGroup ) => ( {
		label: contactGroup.name,
		value: contactGroup.id.toString(),
		group: groupName,
		disabled: !isContactGroupsFeatureAvailable
	} ) )
);

const useFormInviteMembersOptions = ( {
	selectedWorkspaceID,
	isContactGroupsFeatureAvailable,
	formState,
	setFormState
}: UseFormInviteMembersOptionsParams ) => {
	const dispatch = useDispatch();
	const workspaceSystem = new WorkspaceSystem( dispatch );
	const contactGroups = useSelector(
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		state => getWorkspaceContactGroups( state, { workspaceID: selectedWorkspaceID } )
	);

	const options = useMemo( () => {
		const contactGroupsGroupName = isContactGroupsFeatureAvailable
			? CONTACT_GROUPS_GROUP
			: `${CONTACT_GROUPS_GROUP} (Studio Plan)`;

		return [
			ALL_WORKSPACE_MEMBERS_OPTION,
			...( contactGroups.length > 0
				? mapContactGroupsToOptions( contactGroups, contactGroupsGroupName, isContactGroupsFeatureAvailable )
				: [ {
					...NO_CONTACT_GROUPS_OPTION,
					group: contactGroupsGroupName
				} ]
			)
		];
	}, [ contactGroups, isContactGroupsFeatureAvailable ] );

	const onOptionChange = useCallback( ( values: string[] ) => {
		setFormState( prevState => prevState
			.set( 'inviteAllMembers', values.includes( INVITE_WORKSPACE_MEMBERS_ID ) )
			.set( 'inviteContactGroups', values.filter( value => value !== INVITE_WORKSPACE_MEMBERS_ID ).map( id => parseInt( id, 10 ) ) ) );
	}, [ setFormState ] );

	useEffect( () => {
		if ( !selectedWorkspaceID ) { return; }
		workspaceSystem.getContactsGroups( selectedWorkspaceID );
	}, [ selectedWorkspaceID ] );

	const selected = useMemo( () => [
		...( formState.data.inviteAllMembers ? [ INVITE_WORKSPACE_MEMBERS_ID ] : [] ),
		...( formState.data.inviteContactGroups.map( ( id: number ) => id.toString() ) )
	], [ formState.data.inviteAllMembers, formState.data.inviteContactGroups ] );

	return {
		options,
		selected,
		onOptionChange
	};
};

export default useFormInviteMembersOptions;
